import { useTranslation } from "next-i18next";
import React, { useEffect } from "react";

import { getFeatureFlag } from "@/shared/helpers/launch-darkly-server-helper";

import { parallel } from "@/utils/promises";
import serverSideTranslations from "@/utils/serverSideTranslations";

import { SubmitFormPage } from "@/components/submit-form/submit-form-page";

import { ChunkedNav, GenericNavItem } from "@/interfaces/navigation-interface";

import { Locale } from "@/types/locale";
import { GetServerSideProps } from "@/types/page";

import DefaultLayout from "../../layouts/default";
import { getFooterNavigation, getHeaderNavigation } from "../api/store-directory/navigation";

interface LocaleProps {
    locale: Locale;
}

type NavProps = {
    headerNavigationItems: ChunkedNav;
    footerLocalNavItems: ChunkedNav;
    footerRegionalNavItems: Array<Array<GenericNavItem>>;
};

type Props = NavProps & LocaleProps;

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale }) => {
    const isEnabled = await getFeatureFlag("help-centre-enabled", false, locale);

    if (!isEnabled) {
        return { redirect: { destination: `/${locale}`, permanent: false } };
    }

    const parallelProps = await parallel({
        headerNavigationItems: () => getHeaderNavigation(locale),
        footerNavItems: () => getFooterNavigation(locale),
        _nextI18Next: () => serverSideTranslations(locale),
    });

    return {
        props: {
            locale,
            footerLocalNavItems: parallelProps.footerNavItems[0],
            footerRegionalNavItems: parallelProps.footerNavItems[1],
            ...parallelProps,
        },
    };
};

const SubmitForm = ({
    locale,
    headerNavigationItems,
    footerLocalNavItems,
    footerRegionalNavItems,
}: Props): React.ReactNode => {
    const { t } = useTranslation();

    useEffect(() => {
        document.body.className = "helpPage";
    }, []);

    return (
        <DefaultLayout
            currentLocale={locale}
            headerNavigationItems={headerNavigationItems}
            footerLocalNavItems={footerLocalNavItems}
            footerRegionalNavItems={footerRegionalNavItems}
            builderNavLinks={[]}
            builderMessages={[]}
            title={t("help.submitForm.title")}
        >
            <SubmitFormPage locale={locale} />
        </DefaultLayout>
    );
};

export default SubmitForm;
